import { Component } from '@theme/component';
import { offerFor, dimmerQuantity, priceTotals, cartItems, displayPence, formatPounds } from '@theme/dimmer-offer';

/**
 * <dimmer-addon>: the optional LED dimmer box on dimmable mains downlight and
 * bulb pages (snippets/dimmer-addon.liquid).
 *
 * The customer picks at most one of the two dimmers (with plate, or module
 * only) and how many. The box follows the selected variant (fittings where the
 * bulb is a variant only qualify with a dimmable bulb or no bulb), the light
 * quantity and the VAT mode.
 *
 * Like <hex-suspension-addon>, it never adds to the basket itself. It is marked
 * data-cart-addon, and product-main's form and the sticky bar call
 * `itemsFor()`, `linesAdded()` and `explainFailure()` around their existing
 * /cart/add.js request.
 *
 * @typedef {object} AddonConfig
 * @property {Record<string, { offer: boolean, note: boolean, price: number }>} variants
 * @property {Array<{ variantId: number, price: number, available: boolean }>} dimmers
 *
 * @extends {Component<{ data: HTMLScriptElement, choice: HTMLInputElement[], price: HTMLElement[], priceVat: HTMLElement[], note: HTMLElement, selected: HTMLElement, quantity: HTMLInputElement, totalNoun: HTMLElement, totalAmount: HTMLElement, totalVat: HTMLElement, status: HTMLElement }>}
 */
class DimmerAddon extends Component {
  requiredRefs = ['data', 'choice', 'price', 'priceVat', 'note', 'selected', 'quantity', 'totalNoun', 'totalAmount', 'totalVat', 'status'];

  missingText = 'The dimmer could not be added.';

  /**
   * What a ticked dimmer adds, for the sticky bar, or null when none.
   * @type {{ items: string, total: string } | null}
   */
  summary = null;

  /** @type {AddonConfig} */
  #config;
  /** @type {AbortController | undefined} */
  #abort;

  connectedCallback() {
    super.connectedCallback();
    this.#config = JSON.parse(this.refs.data.textContent || '{}');

    this.#abort = new AbortController();
    const { signal } = this.#abort;
    const form = this.closest('form');
    // The light's quantity box and − / + buttons (their own handler has already
    // changed the value by the time the click bubbles to the form).
    form?.addEventListener('input', this.#onFormChange, { signal });
    form?.addEventListener('change', this.#onFormChange, { signal });
    form?.addEventListener('click', (event) => {
      if (/** @type {Element} */ (event.target).closest('.pm-cart-form__qty-btn')) this.#refresh();
    }, { signal });
    window.addEventListener('vatPreferenceChanged', this.#refresh, { signal });
    window.addEventListener('mainVariantChange', this.#refresh, { signal });

    this.#refresh();
  }

  disconnectedCallback() {
    super.disconnectedCallback();
    this.#abort?.abort();
  }

  /** One dimmer at a time: ticking one unticks the other. */
  onChoose(event) {
    const picked = /** @type {HTMLInputElement} */ (event.target);
    if (picked.checked) {
      for (const box of this.refs.choice) if (box !== picked) box.checked = false;
    }
    this.#hideStatus();
    this.#refresh();
  }

  /** @param {number} step */
  onStep(step) {
    this.refs.quantity.value = String(Math.max(1, dimmerQuantity(this.refs.quantity.value) + step));
    this.#refresh();
  }

  onQuantity() {
    this.#refresh();
  }

  /**
   * Lines for the add-to-basket request, or null to add the light alone.
   * @param {{ id: number, quantity: number, properties?: Record<string, string> }} lightLine
   * @returns {ReturnType<typeof cartItems> | null}
   */
  itemsFor(lightLine) {
    const dimmer = this.#chosen();
    if (!dimmer || !offerFor(this.#config.variants, lightLine.id).offer) return null;
    return cartItems(lightLine, { variantId: dimmer.variantId, quantity: this.#dimmerCount() });
  }

  /**
   * True when a request carrying these items really added the dimmer.
   * @param {any} response
   */
  linesAdded(response) {
    const dimmer = this.#chosen();
    const lines = Array.isArray(response?.items) ? response.items : [];
    return !!dimmer && lines.some((line) => Number(line.variant_id) === dimmer.variantId);
  }

  /**
   * After a failed or partial add, reads the basket and says what it holds.
   * The selection is left as it was.
   * @param {string} shopifyMessage
   * @param {number} lightVariantId
   * @param {boolean} [showInBox] Also show it inside the box (for callers with no error slot of their own)
   * @returns {Promise<string>}
   */
  async explainFailure(shopifyMessage, lightVariantId, showInBox = false) {
    let text = (shopifyMessage || 'Could not add to basket.').trim();
    try {
      const res = await fetch('/cart.js', { headers: { Accept: 'application/json' } });
      const cart = await res.json();
      const count = (/** @type {number} */ id) =>
        (cart.items || []).filter((/** @type {any} */ i) => Number(i.variant_id) === id).reduce((n, /** @type {any} */ i) => n + i.quantity, 0);
      const lights = count(Number(lightVariantId));
      const dimmers = this.#config.dimmers.reduce((n, d) => n + count(d.variantId), 0);
      const noun = this.querySelector('[data-basket-noun]')?.getAttribute('data-basket-noun') || 'of these lights';
      text += ' Your basket now has ' + lights + ' ' + noun + ' and ' + dimmers + (dimmers === 1 ? ' dimmer.' : ' dimmers.');
    } catch {
      text += ' Please check your basket before trying again.';
    }
    if (showInBox) {
      this.refs.status.textContent = text;
      this.refs.status.hidden = false;
    }
    return text;
  }

  #chosen() {
    const box = this.refs.choice.find((b) => b.checked && !b.disabled);
    const dimmer = box ? this.#config.dimmers[Number(box.value)] : null;
    return dimmer && dimmer.available ? dimmer : null;
  }

  #dimmerCount() {
    return dimmerQuantity(this.refs.quantity.value);
  }

  #hideStatus() {
    this.refs.status.hidden = true;
    this.refs.status.textContent = '';
  }

  /** @param {Event} event */
  #onFormChange = (event) => {
    // The box's own inputs have their own handlers.
    if (this.contains(/** @type {Node} */ (event.target))) return;
    this.#refresh();
  };

  #lightVariantId() {
    const input = /** @type {HTMLInputElement | null} */ (this.closest('form')?.querySelector('input[name="id"]'));
    return Number(input?.value) || 0;
  }

  #lightQuantity() {
    const input = /** @type {HTMLInputElement | null} */ (this.closest('form')?.querySelector('input[name="quantity"]'));
    return Math.max(1, parseInt(input?.value || '1', 10) || 1);
  }

  #vatMode() {
    try {
      return localStorage.getItem('vatPreference') === 'ex' ? 'ex' : 'inc';
    } catch {
      return 'inc';
    }
  }

  #refresh = () => {
    const config = this.#config;
    if (!config) return;
    const variantId = this.#lightVariantId();
    const { offer, note } = offerFor(config.variants, variantId);

    this.hidden = !offer;
    if (!offer) for (const box of this.refs.choice) box.checked = false;
    this.refs.note.hidden = !note;

    const vat = this.#vatMode();
    const vatLabel = vat === 'ex' ? 'ex VAT' : 'inc VAT';
    config.dimmers.forEach((dimmer, i) => {
      if (this.refs.price[i]) this.refs.price[i].textContent = '+' + formatPounds(displayPence(dimmer.price, vat));
      if (this.refs.priceVat[i]) this.refs.priceVat[i].textContent = vatLabel;
    });

    const dimmer = this.#chosen();
    this.refs.selected.hidden = !dimmer;
    /** @type {{ items: string, total: string } | null} */
    let summary = null;

    if (dimmer) {
      const dimmers = this.#dimmerCount();
      const lights = this.#lightQuantity();
      const lightPrice = config.variants[String(variantId)]?.price ?? 0;
      const { total } = priceTotals({ lightPrice, lightQuantity: lights, dimmerPrice: dimmer.price, dimmerQuantity: dimmers });
      const dimmerNoun = dimmers === 1 ? 'dimmer' : 'dimmers';
      const lead = this.querySelector('[data-item-one]');
      const lightNoun = (lights === 1 ? lead?.getAttribute('data-item-one') : lead?.getAttribute('data-item-many')) || 'lights';
      const amount = formatPounds(displayPence(total, vat));
      this.refs.totalNoun.textContent = dimmerNoun;
      this.refs.totalAmount.textContent = amount;
      this.refs.totalVat.textContent = vatLabel;
      summary = { items: lights + ' ' + lightNoun + ' + ' + dimmers + ' ' + dimmerNoun, total: amount + ' ' + vatLabel };
    }

    this.summary = summary;
    this.dispatchEvent(new CustomEvent('cartaddon:change', { bubbles: true }));
  };
}

if (!customElements.get('dimmer-addon')) {
  customElements.define('dimmer-addon', DimmerAddon);
}
